import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { scanFeedback } from "@/lib/feedback";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { StockMoveForm } from "@/components/StockDialogs";
import { ArrowDownToLine, ArrowUpFromLine, Boxes, ScanLine, Printer, AlertTriangle } from "lucide-react";

export default function StockScan() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [type, setType] = useState("in");
  const load = useCallback(() => api.get(`/stock/${id}`).then((r) => setData(r.data)).catch(() => setErr("Stock item not found")), [id]);
  useEffect(() => { load(); }, [load]);

  if (err) return <div className="min-h-screen flex items-center justify-center text-muted-foreground" data-testid="stock-scan-error">{err}</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading...</div>;
  const { item, ledger } = data;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-4" data-testid="stock-scan-page">
      <div className="max-w-md mx-auto space-y-4">
        <div className="flex items-center justify-between gap-3 pt-2">
          <div className="flex items-center gap-3"><img src="/rozfa-logo-white.png" alt="Rozfa" className="h-8 w-auto" /><div className="text-xs font-mono text-slate-400 uppercase tracking-widest">Stock Scan</div></div>
          <div className="text-xs text-slate-400">{user?.name}</div>
        </div>
        <Card className="p-5 bg-white text-slate-900 space-y-2">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="text-xs font-mono text-muted-foreground">{item.sku}</div>
              <div className="text-2xl font-display font-black leading-tight" data-testid="stock-scan-name">{item.name}</div>
              <div className="text-xs text-muted-foreground">{item.category} • {item.supplier || "no supplier"} • {item.location || "no location"}</div>
            </div>
            <Boxes className="w-8 h-8 text-cyan-600" />
          </div>
          <div className={`rounded-lg p-3 flex items-center justify-between ${item.low ? "bg-rose-50 border border-rose-300" : "bg-slate-100"}`}>
            <div className="text-sm">Current balance</div>
            <div className="font-mono font-black text-3xl" data-testid="stock-scan-qty">{item.quantity} <span className="text-sm font-normal">{item.unit}</span></div>
          </div>
          {item.low && <div className="text-xs text-rose-600 flex items-center gap-1" data-testid="stock-scan-low"><AlertTriangle className="w-3.5 h-3.5" /> Below minimum level ({item.min_qty} {item.unit}) — reorder</div>}
        </Card>

        <Card className="p-4 bg-white text-slate-900 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => setType("in")} className={`h-14 rounded-lg font-bold text-lg flex items-center justify-center gap-2 border-2 ${type === "in" ? "bg-emerald-600 text-white border-emerald-600" : "border-emerald-600 text-emerald-700"}`} data-testid="stock-scan-type-in"><ArrowDownToLine className="w-5 h-5" /> Stock In</button>
            <button type="button" onClick={() => setType("out")} className={`h-14 rounded-lg font-bold text-lg flex items-center justify-center gap-2 border-2 ${type === "out" ? "bg-rose-600 text-white border-rose-600" : "border-rose-600 text-rose-700"}`} data-testid="stock-scan-type-out"><ArrowUpFromLine className="w-5 h-5" /> Stock Out</button>
          </div>
          <StockMoveForm key={type} item={item} type={type} compact onSaved={() => { scanFeedback("success"); load(); }} />
        </Card>

        <Card className="p-4 bg-white text-slate-900">
          <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">Recent movements</div>
          <div className="divide-y text-sm" data-testid="stock-scan-ledger">
            {ledger.length === 0 && <div className="text-muted-foreground py-2">No movements yet</div>}
            {ledger.map((l) => (
              <div key={l.id} className="py-1.5 flex items-center justify-between gap-2">
                <div className="min-w-0"><div className="text-xs text-muted-foreground">{formatDateTime(l.at)} • {l.user_name}</div><div className="text-xs truncate">{l.job_number && <span className="font-mono font-semibold mr-1">{l.job_number}</span>}{l.note}</div></div>
                <div className="text-right shrink-0"><Badge className={l.type === "in" ? "bg-emerald-600" : l.type === "out" ? "bg-rose-600" : "bg-amber-500"}>{l.quantity > 0 ? "+" : ""}{l.quantity}</Badge><div className="text-[10px] font-mono text-muted-foreground">bal {l.balance}</div></div>
              </div>
            ))}
          </div>
        </Card>

        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" onClick={() => nav("/stock/bulk")} className="h-11 border-slate-700 text-slate-200 bg-transparent hover:bg-slate-800" data-testid="stock-scan-bulk"><ScanLine className="w-4 h-4 mr-2" /> Bulk Camera Scan</Button>
          <Button variant="outline" onClick={() => nav(`/stock/labels?ids=${item.id}`)} className="h-11 border-slate-700 text-slate-200 bg-transparent hover:bg-slate-800" data-testid="stock-scan-label"><Printer className="w-4 h-4 mr-2" /> Print QR Label</Button>
        </div>
        <Button variant="ghost" onClick={() => nav("/stock")} className="w-full text-slate-400" data-testid="stock-scan-back">Back to Stock list</Button>
      </div>
    </div>
  );
}
